import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Config, ApiKeys, PreviewData } from '../types';
import { api } from '../utils/api';
import { VestaboardPreview } from './VestaboardPreview';

export function Dashboard() {
  const { user, logout } = useAuth();
  const [config, setConfig] = useState<Config | null>(null);
  const [apiKeys, setApiKeys] = useState<ApiKeys | null>(null);
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [stockInput, setStockInput] = useState('');
  const [cryptoInput, setCryptoInput] = useState('');

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await api.getConfig();
      setConfig(response.config);
      setApiKeys(response.apiKeys);

      // Initialize input fields
      if (response.config) {
        setStockInput(response.config.stocks.join(', '));
        setCryptoInput(response.config.crypto.join(', '));
      }

      // Generate initial preview (non-blocking)
      if (response.config) {
        try {
          const previewData = await api.generatePreview(response.config);
          setPreview(previewData);
        } catch (previewErr) {
          console.warn('Failed to generate initial preview:', previewErr);
          // Don't fail the entire config load if preview fails
        }
      }
    } catch (err: any) {
      setError('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    if (!config || !apiKeys) return;

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      // Only send API keys that don't contain *** (i.e., that have been changed)
      const updatedApiKeys: Partial<ApiKeys> = {};
      Object.entries(apiKeys).forEach(([key, value]) => {
        if (value && !value.includes('***')) {
          updatedApiKeys[key as keyof ApiKeys] = value;
        }
      });

      await api.updateConfig(config, updatedApiKeys);
      setSuccess('Configuration saved successfully');

      // Refresh preview
      const previewData = await api.generatePreview(config);
      setPreview(previewData);
    } catch (err: any) {
      setError(err.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const generatePreview = async () => {
    if (!config) return;

    try {
      const previewData = await api.generatePreview(config);
      setPreview(previewData);
    } catch (err: any) {
      setError('Failed to generate preview');
    }
  };

  const sendToVestaboard = async () => {
    if (!apiKeys?.vestaboard) {
      setError('Vestaboard API key not configured');
      return;
    }

    setSending(true);
    setError('');
    setSuccess('');

    try {
      const response = await api.sendToVestaboard();
      setSuccess('Message sent to Vestaboard successfully!');
    } catch (err: any) {
      setError(err.message || 'Failed to send message to Vestaboard');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return <div style={styles.loading}>Loading...</div>;
  }

  if (!config || !apiKeys) {
    return <div style={styles.error}>Failed to load configuration</div>;
  }

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Vestaboard Dashboard</h1>
        <div style={styles.userInfo}>
          <span>Welcome, {user?.username}</span>
          <button onClick={logout} style={styles.logoutButton}>Logout</button>
        </div>
      </header>

      {preview && <VestaboardPreview characters={preview.characters} />}

      <div style={styles.content}>
        {error && <div style={styles.errorMessage}>{error}</div>}
        {success && <div style={styles.successMessage}>{success}</div>}

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>API Configuration</h2>
          <div style={styles.grid}>
            <div style={styles.field}>
              <label style={styles.label}>Vestaboard API Key</label>
              <input
                type="text"
                value={apiKeys.vestaboard}
                onChange={(e) => setApiKeys({ ...apiKeys, vestaboard: e.target.value })}
                style={styles.input}
                placeholder="Enter Vestaboard API key"
              />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>OpenWeather API Key</label>
              <input
                type="text"
                value={apiKeys.openweather}
                onChange={(e) => setApiKeys({ ...apiKeys, openweather: e.target.value })}
                style={styles.input}
                placeholder="Enter OpenWeather API key"
              />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Polygon.io API Key</label>
              <input
                type="text"
                value={apiKeys.polygon}
                onChange={(e) => setApiKeys({ ...apiKeys, polygon: e.target.value })}
                style={styles.input}
                placeholder="Enter Polygon.io API key"
              />
            </div>
          </div>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Display Configuration</h2>
          <div style={styles.grid}>
            <div style={styles.field}>
              <label style={styles.label}>Update Interval (minutes)</label>
              <input
                type="number"
                value={config.updateInterval}
                onChange={(e) => setConfig({ ...config, updateInterval: parseInt(e.target.value) || 15 })}
                style={styles.input}
                min="5"
                max="60"
              />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Weather Location</label>
              <input
                type="text"
                value={config.weatherLocation}
                onChange={(e) => setConfig({ ...config, weatherLocation: e.target.value })}
                style={styles.input}
                placeholder="New York, NY"
              />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Timezone</label>
              <select
                value={config.timezone}
                onChange={(e) => setConfig({ ...config, timezone: e.target.value })}
                style={styles.select}
              >
                <option value="America/New_York">Eastern Time</option>
                <option value="America/Chicago">Central Time</option>
                <option value="America/Denver">Mountain Time</option>
                <option value="America/Los_Angeles">Pacific Time</option>
              </select>
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Date Format</label>
              <select
                value={config.dateFormat}
                onChange={(e) => setConfig({ ...config, dateFormat: e.target.value })}
                style={styles.select}
              >
                <option value="MM/DD">MM/DD</option>
                <option value="DD/MM">DD/MM</option>
                <option value="MMM DD">MMM DD</option>
              </select>
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Time Format</label>
              <select
                value={config.timeFormat}
                onChange={(e) => setConfig({ ...config, timeFormat: e.target.value as '12h' | '24h' })}
                style={styles.select}
              >
                <option value="12h">12 Hour</option>
                <option value="24h">24 Hour</option>
              </select>
            </div>
          </div>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Assets</h2>
          <div style={styles.grid}>
            <div style={styles.field}>
              <label style={styles.label}>Stock Symbols (max 4 total)</label>
              <input
                type="text"
                value={stockInput}
                onChange={(e) => {
                  setStockInput(e.target.value);
                  // Update config with parsed values
                  const symbols = e.target.value.split(',').map(s => s.trim()).filter(s => s);
                  setConfig({ ...config, stocks: symbols });
                }}
                style={styles.input}
                placeholder="AAPL, GOOGL, MSFT"
              />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Crypto IDs (CoinGecko format)</label>
              <input
                type="text"
                value={cryptoInput}
                onChange={(e) => {
                  setCryptoInput(e.target.value);
                  // Update config with parsed values
                  const symbols = e.target.value.split(',').map(s => s.trim()).filter(s => s);
                  setConfig({ ...config, crypto: symbols });
                }}
                style={styles.input}
                placeholder="bitcoin, ethereum"
              />
            </div>
          </div>
        </div>

        <div style={styles.actions}>
          <button onClick={generatePreview} style={styles.previewButton}>
            Update Preview
          </button>
          <button onClick={sendToVestaboard} style={styles.sendButton} disabled={sending || !apiKeys?.vestaboard}>
            {sending ? 'Sending...' : 'Send to Vestaboard'}
          </button>
          <button onClick={saveConfig} style={styles.saveButton} disabled={saving}>
            {saving ? 'Saving...' : 'Save Configuration'}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
  },
  header: {
    backgroundColor: 'white',
    padding: '20px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    margin: 0,
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#333',
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
  },
  logoutButton: {
    padding: '8px 16px',
    backgroundColor: '#dc3545',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  content: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '20px',
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    fontSize: '18px',
  },
  error: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    color: '#dc3545',
    fontSize: '18px',
  },
  errorMessage: {
    color: '#dc3545',
    padding: '10px',
    backgroundColor: '#f8d7da',
    border: '1px solid #f5c6cb',
    borderRadius: '4px',
    marginBottom: '20px',
  },
  successMessage: {
    color: '#155724',
    padding: '10px',
    backgroundColor: '#d4edda',
    border: '1px solid #c3e6cb',
    borderRadius: '4px',
    marginBottom: '20px',
  },
  section: {
    backgroundColor: 'white',
    padding: '30px',
    borderRadius: '8px',
    marginBottom: '20px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  sectionTitle: {
    margin: '0 0 20px 0',
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#333',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '20px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
  },
  label: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#555',
  },
  input: {
    padding: '12px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '16px',
  },
  select: {
    padding: '12px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '16px',
    backgroundColor: 'white',
  },
  actions: {
    display: 'flex',
    gap: '15px',
    justifyContent: 'center',
    marginTop: '30px',
  },
  previewButton: {
    padding: '12px 24px',
    backgroundColor: '#6c757d',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '16px',
    cursor: 'pointer',
  },
  sendButton: {
    padding: '12px 24px',
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '16px',
    cursor: 'pointer',
  },
  saveButton: {
    padding: '12px 24px',
    backgroundColor: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '16px',
    cursor: 'pointer',
  },
};